import { formatTry, type CheckoutStartDto, type CheckoutStatusDto, type FakeTestCard } from '@dbrain/shared';
import { Alert, Button, Dialog, Icons } from '@dbrain/ui';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { checkoutStatus } from '../../api/billing.js';
import { api } from '../../api/client.js';
import { ErrorAlert } from '../ErrorAlert.js';

/**
 * Phase 16c (B2cOdemeKart): the provider's own card form in a dialog. The card never reaches Oppy: the form is the
 * provider's (iyzico in Phase 16d). With the fake provider (development and tests, never in production) it is Oppy's
 * test form, marked as one, with a card that pays, one that pays now and declines renewals, and one that is declined.
 * When the form is done the dialog asks Oppy for the order's state (Oppy asks the provider) and closes on its own.
 */
const TEST_CARDS: { card: FakeTestCard; last4: string }[] = [
  { card: 'success', last4: '4242' },
  { card: 'renewal_declines', last4: '0341' },
  { card: 'decline', last4: '0002' },
];

export function HostedFormDialog({
  checkout,
  description,
  onPaid,
  onFailed,
  onClose,
}: {
  checkout: CheckoutStartDto;
  description: string;
  onPaid: (status: CheckoutStatusDto) => void;
  onFailed: (status: CheckoutStatusDto) => void;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const [card, setCard] = useState<FakeTestCard>('success');
  const [phase, setPhase] = useState<'form' | 'waiting'>('form');
  const [error, setError] = useState<unknown>(null);
  const alive = useRef(true);
  useEffect(
    () => () => {
      alive.current = false;
    },
    [],
  );

  /** the order's state from Oppy, a few times while the provider's word is on its way */
  const settle = async () => {
    for (let i = 0; i < 30 && alive.current; i++) {
      const status = await checkoutStatus(checkout.orderId);
      if (status.status === 'paid') return onPaid(status);
      if (status.status === 'failed' || status.status === 'expired') return onFailed(status);
      await new Promise((resolve) => setTimeout(resolve, 1_000));
    }
  };

  const pay = async (cancel = false) => {
    setPhase('waiting');
    setError(null);
    try {
      await api('POST', `/api/billing/fake/sessions/${checkout.form.token}/pay`, { card, ...(cancel ? { cancel: true } : {}) });
      await settle();
    } catch (failure) {
      if (!alive.current) return;
      setError(failure);
      setPhase('form');
    }
  };

  const amount = formatTry(checkout.amountKurus, i18n.language);
  return (
    <Dialog title={t('b2cBilling.form.title')} onClose={onClose} closeLabel={t('common.close')} size="md" className="hosted-form" data-testid="hosted-form">
      <div className="hosted-form__frame">
        {checkout.form.kind === 'fake' ? (
          <div className="fake-form" data-testid="fake-form">
            <p className="fake-form__badge">
              <span className="ui-badge ui-badge--warning">{t('b2cBilling.form.test.badge')}</span>
              <span>{t('b2cBilling.form.test.note')}</span>
            </p>
            <fieldset className="fake-form__cards" disabled={phase === 'waiting'}>
              <legend className="fake-form__legend">{t('b2cBilling.form.test.cards')}</legend>
              {TEST_CARDS.map((option) => (
                <label key={option.card} className="fake-form__card">
                  <input type="radio" name="test-card" value={option.card} checked={card === option.card} onChange={() => setCard(option.card)} data-testid={`fake-card-${option.card}`} />
                  <span className="fake-form__number">•••• {option.last4}</span>
                  <span className="fake-form__what">{t(`b2cBilling.form.test.card.${option.card}`)}</span>
                </label>
              ))}
            </fieldset>
            <ErrorAlert error={error} />
            <div className="fake-form__actions">
              <Button variant="secondary" onClick={() => void pay(true)} disabled={phase === 'waiting'} data-testid="fake-cancel">
                {t('b2cBilling.form.test.cancel')}
              </Button>
              <Button onClick={() => void pay()} busy={phase === 'waiting'} data-testid="fake-pay">
                {checkout.amountKurus > 0 ? t('b2cBilling.form.test.pay', { amount }) : t('b2cBilling.form.test.saveCard')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="hosted-form__placeholder">
            <span className="hosted-form__icon" aria-hidden="true">
              <Icons.key size={20} />
            </span>
            <Alert tone="info" title={t('b2cBilling.form.placeholder.title')}>
              {t('b2cBilling.form.placeholder.body')}
            </Alert>
          </div>
        )}
      </div>
      <p className="hosted-form__line">
        <span>{description}</span>
        <strong>{checkout.amountKurus > 0 ? amount : t('b2cBilling.form.noCharge')}</strong>
      </p>
      <p className="hosted-form__note">{t('b2cBilling.form.note')}</p>
    </Dialog>
  );
}
